<template>
  <b-field
    v-bind="$attrs"
    :label="label"
    :type="type"
    class="form_field"
    custom-class="form_field--label"
    :message="helpText"
  >
    <div class="tiptap-wrapper">
      <div
        class="tiptap"
        :class="{ 'is-danger' : validationErrors }"
      >
        <!-- Menu Items-->
        <div
          v-if="menuItems.length"
          class="tiptap_menu"
        >
          <template v-for="(item, index) in menuItems">
            <span
              class="tiptap_divider"
              v-if="item === 'divider'"
              :key="index"
            />

            <button
              v-if="item !== 'divider' && item.isMenuItem !== false"
              :class="['tiptap_menu--item', { 'is-active': editor.isActive(item.activeName) }]"
              @click="item.action"
              :title="item.title"
              :key="index"
              tabindex="-1"
            >
              <i class="material-icons">{{ item.icon }}</i>
            </button>
          </template>
        </div>

        <editor-content :class="{'is-short': isShort,'is-tall': isTall, 'is-single-line': isSingleLine}" :editor="editor" v-model="value"/>

        <!-- Character count -->
        <div v-if="editor" class="tiptap_hints">
          <p v-if="editorMessage" class="tiptap_hints--message">{{ editorMessage }}</p>
          <p v-if="showCharCount" class="tiptap_hints--count">
            chars: {{ editor.storage.characterCount.characters() }}
            <span v-if="charCountLimit">/{{ charCountLimit }}</span>
          </p>
        </div>
      </div>

      <form-validation-errors :validation-errors="validationErrors"></form-validation-errors>
    </div>
  </b-field>
</template>

<script>
import tippy from "tippy.js";
import FormField from "../FormField";
import {FormFieldMixin} from "../FormField.mixin";
import {ENUMS} from "../../../services/enums";
import {Editor, EditorContent, VueRenderer} from "@tiptap/vue-2";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Paragraph from "@tiptap/extension-paragraph";
import MentionList from "./Mention/MentionList";
import {appendModal} from "../../../services/modals";
import SnippetsSelectModal from "./Snippets/SnippetsSelectModal";
import {Shortcodes} from "./ShortCodes";
import {Mention} from "./Mention";
import ShortcodeList from "./ShortCodes/ShortcodeList";
import EmailSignatureModal from "./EmailSignatue/EmailSignatureModal";
import LocalStorage from "../../../services/localStorage";
import FormValidationErrors from "../FormValidationErrors";
import LinkModal from "./Link/LinkModal";
import {CharacterCount} from "@tiptap/extension-character-count";

export default {
  name: "FormFieldEditor",
  components: {FormValidationErrors, FormField, EditorContent, MentionList},
  mixins: [FormFieldMixin],
  props: {
    value: {},
    label: {
      type: String,
    },
    type: {
      type: String,
    },
    shortCodeType: {
      type: String,
    },
    placeholder: {
      type: String
    },
    validationErrors: {
      type: [
        String,
        Array,
        undefined
      ],
    },
    helpText: {
      type: String
    },
    isTall: {
      type: Boolean,
      default: false
    },
    isShort: {
      type: Boolean,
      default: false
    },
    isSingleLine: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      forId: this.$utils.generateClientId(),
      editor: null,
      editorMessage: null,
      showCharCount: false,
      charCountLimit: null,
      menuItems: [],

      baseExtensions: [
        Placeholder,
        StarterKit,
        Underline,
        Highlight,
        Link.configure({
          openOnClick: false,
        }),
      ],
      addLinkAction: () => {
        // const previousUrl = this.editor.getAttributes("link").href;
        // const url = window.prompt("URL", previousUrl);

        let url = this.editor.getAttributes("link").href;

        let linkModal = appendModal(
          LinkModal,
          this,
          {url: url}
        );

        linkModal.$on("updateLink", (link) => {
          url = link;

          // cancelled
          if (url === null) {
            return;
          }

          // empty
          if (url === "") {
            this.editor
              .chain()
              .focus()
              .extendMarkRange("link")
              .unsetLink()
              .run();

            return;
          }

          // make sure the url isn't appended to the
          if (!url.startsWith("http")) {
            url = "//" + url;
          }

          // update link
          this.editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({href: url})
            .run();
        });
      },
      baseMenuItems: [
        {
          icon: "format_bold",
          title: "Bold",
          activeName: "bold",
          action: () => this.editor.chain().focus().toggleBold().run(),
        },
        {
          icon: "format_italic",
          title: "Italic",
          activeName: "italic",
          action: () => this.editor.chain().focus().toggleItalic().run(),
        },
        {
          icon: "format_underlined",
          title: "Underline",
          activeName: "underline",
          action: () => this.editor.chain().focus().toggleUnderline().run(),
        },
        {
          icon: "format_strikethrough",
          title: "Strike",
          activeName: "strike",
          action: () => this.editor.chain().focus().toggleStrike().run(),
        },
        {
          icon: "mode",
          title: "Highlight",
          activeName: "highlight",
          action: () => this.editor.chain().focus().toggleHighlight().run(),
        },
        "divider",
        {
          icon: "format_list_bulleted",
          title: "Bullet List",
          activeName: "bulletList",
          action: () => this.editor.chain().focus().toggleBulletList().run(),
        },
        {
          icon: "format_list_numbered",
          title: "Ordered List",
          activeName: "orderedList",
          action: () => this.editor.chain().focus().toggleOrderedList().run(),
        },
        // {
        //   icon: 'double-quotes-l',
        //   title: 'Horizontal Rule',
        //   action: () => this.editor.chain().focus().setHorizontalRule().run(),
        // },
        // {
        //   icon: 'format-clear',
        //   title: 'Clear Format',
        //   action: () => this.editor.chain().focus().clearNodes().unsetAllMarks().run(),
        // },
        // {
        //   icon: 'arrow-go-back-line',
        //   title: 'Undo',
        //   action: () => this.editor.chain().focus().undo().run(),
        // },
        // {
        //   icon: 'arrow-go-forward-line',
        //   title: 'Redo',
        //   action: () => this.editor.chain().focus().redo().run(),
        // },
      ]
    };
  },
  computed: {
    snippets() {
      return this.$store.getters.snippets;
    },
    users() {
      return this.$store.getters.activeUsers;
    },
    accountId() {
      return this.$store.getters.account.id;
    },
  },
  watch: {
    value(value) {
      // HTML
      const isSame = this.editor.getHTML() === value

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) return

      this.editor.commands.setContent(value, false)
    }
  },
  mounted() {
    this.prepEditor();
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    getSnippets(snippetType) {
      const snippets = this.snippets.filter(snippet => snippet.type === snippetType)
      return snippets.sort(function(a, b){
        if(a.label < b.label) { return -1; }
        if(a.label > b.label) { return 1; }
        return 0;
      });
    },

    buildMenuItem(title, icon, activeName, onClick) {
      return {title: title, icon: icon, activeName: activeName, action: onClick};
    },

    prepEditor() {
      let shortcodes = [];
      if (this.shortCodeType === "followUp") {
        shortcodes = this.$store.state.enums.shortcodesFollowUpEnum;
      } else if (this.shortCodeType === "record") {
        shortcodes = this.$store.state.enums.shortcodesRecordEnum;
      }

      let menuItems = this.baseMenuItems;
      let extensions = this.baseExtensions;
      let textSnippetMenuItem;

      const textCharacterLimit = 1000;

      switch (this.type) {
        case "note":
          let noteSnippetMenuItem = this.buildMenuItem(
            "Note Snippets",
            "assignment",
            "noteSnippets",
            () => {
              let selectSnippetModal = appendModal(
                SnippetsSelectModal,
                this,
                {snippets: this.getSnippets(ENUMS.SnippetTypeEnum.Note.value)}
              );

              selectSnippetModal.$on("selectedSnippet", (snippet) => {
                this.editor.chain().focus("start").insertContent(snippet.body).run();
                this.editor.commands.focus("end");
              });
            });

          menuItems.push("divider");
          menuItems.push(noteSnippetMenuItem);

          menuItems.push("divider");
          menuItems.push(this.buildMenuItem(
            "Link",
            "link",
            "link",
            this.addLinkAction));

          extensions.push(this.buildMentionsExtension());

          return this.buildEditor(menuItems, extensions);
        case "shortCodesOnly":
          menuItems = [];
          extensions.push(this.buildShortCodeExtension(shortcodes));
          return this.buildEditor(menuItems, extensions);
        case "email":
          let emailSnippetMenuItem = this.buildMenuItem(
            "Email Snippets",
            "assignment",
            "emailSnippets",
            () => {
              let selectSnippetModal = appendModal(SnippetsSelectModal, this, {snippets: this.getSnippets(ENUMS.SnippetTypeEnum.Email.value)});
              selectSnippetModal.$on("selectedSnippet", (snippet) => {
                this.$emit('email-snippet-selected', snippet)
                this.editor.chain().focus("start").insertContent(snippet.body).run();
                this.editor.commands.focus("end");
              });
            });

          menuItems.push("divider");
          menuItems.push(emailSnippetMenuItem);

          menuItems.push("divider");
          menuItems.push(this.buildMenuItem(
            "Link",
            "link",
            "link",
            this.addLinkAction));

          extensions.push(this.buildShortCodeExtension(shortcodes));

          return this.buildEditor(menuItems, extensions);
        case "textContactWithoutRecord":
          menuItems = [];

          this.charCountLimit = textCharacterLimit;
          extensions.push(this.buildCharLimitExtension(this.charCountLimit));
          this.showCharCount = true;

          textSnippetMenuItem = this.buildMenuItem(
            "Text Snippets",
            "assignment",
            "textSnippets",
            () => {
              let selectSnippetModal = appendModal(SnippetsSelectModal, this, {snippets: this.getSnippets(ENUMS.SnippetTypeEnum.Text.value)});
              selectSnippetModal.$on("selectedSnippet", (snippet) => {
                this.editor.chain().focus("start").insertContent(snippet.body).run();
                this.editor.commands.focus("end");
              });
            });

          menuItems.push(textSnippetMenuItem);
          this.editorMessage = "Select a Lead in order to use shortcodes";

          return this.buildEditor(menuItems, extensions);
        case "textContactWithRecord":
          menuItems = [];

          this.charCountLimit = textCharacterLimit;
          extensions.push(this.buildCharLimitExtension(this.charCountLimit));
          this.showCharCount = true;

          textSnippetMenuItem = this.buildMenuItem(
            "Text Snippets",
            "assignment",
            "textSnippets",
            () => {
              let selectSnippetModal = appendModal(SnippetsSelectModal, this, {snippets: this.getSnippets(ENUMS.SnippetTypeEnum.Text.value)});
              selectSnippetModal.$on("selectedSnippet", (snippet) => {
                this.editor.chain().focus("start").insertContent(snippet.body).run();
                this.editor.commands.focus("end");
              });
            });

          menuItems.push(textSnippetMenuItem);
          extensions.push(this.buildShortCodeExtension(shortcodes));

          return this.buildEditor(menuItems, extensions);
        case "snippetNote":
          menuItems.push("divider");
          menuItems.push(this.buildMenuItem(
            "Link",
            "link",
            "link",
            this.addLinkAction));

          extensions.push(this.buildMentionsExtension());

          return this.buildEditor(menuItems, extensions);
        case "snippetText":
          extensions.push(this.buildShortCodeExtension(shortcodes));
          this.charCountLimit = textCharacterLimit;
          extensions.push(this.buildCharLimitExtension(this.charCountLimit));
          this.showCharCount = true;
          return this.buildEditor([], extensions);
        case "snippetEmail":
          extensions.push(this.buildShortCodeExtension(shortcodes));
          return this.buildEditor(menuItems, extensions);
        case "signature":
          let signatureMenuItem = this.buildMenuItem(
            "Image",
            "image",
            "emailSignature",
            () => {
              let emailSignatureModal = appendModal(EmailSignatureModal, this, {user: LocalStorage.getAuthUser()});
              emailSignatureModal.$on("uploadedEmailSignature", (emailSignatureUrl) => {
                this.editor.chain().focus().setImage({src: emailSignatureUrl}).run();
              });
            });

          menuItems.push(signatureMenuItem);
          extensions.push(Image);

          menuItems.push("divider");
          menuItems.push(this.buildMenuItem(
            "Link",
            "link",
            "link",
            this.addLinkAction));

          return this.buildEditor(menuItems, extensions);
        case "appointment":
          menuItems.push("divider");
          menuItems.push(this.buildMenuItem(
            "Link",
            "link",
            "link",
            this.addLinkAction));

          return this.buildEditor(menuItems, extensions);
        default:
          return this.buildEditor(menuItems, extensions);
      }
    },

    buildEditor(menuItems, extensions) {
      // Set the Menu Items
      this.menuItems = menuItems;

      // Set the Editor Handle
      this.editor = new Editor({
        content: this.value,
        extensions: extensions,
        onUpdate: () => {
          // HTML
          this.$emit("input", this.editor.getHTML());

          // JSON
          // this.$emit('input', this.editor.getJSON())
        },
      });
    },

    clear() {
      this.editor.commands.clearContent(true);
    },

    buildCharLimitExtension(limit) {
      return CharacterCount.configure({
        limit: limit,
      });
    },

    buildMentionsExtension() {
      this.editorMessage = "Use @ to mention a team member";

      return Mention.configure({
        HTMLAttributes: {
          class: "mention",
        },
        suggestion: {
          users: this.users,
          items: editor => {
            return this.users
              .filter(u => u.full_name.toLowerCase().startsWith(editor.query.toLowerCase()));
          },
          render: () => {
            let component;
            let popup;

            return {
              onStart: props => {
                component = new VueRenderer(MentionList, {
                  parent: this,
                  propsData: props,
                });

                popup = tippy("body", {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  placement: "bottom-start",
                });
              },
              onUpdate(props) {
                component.updateProps(props);

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },
              onKeyDown(props) {
                return component.ref.onKeyDown(props);
              },
              onExit() {
                popup[0].destroy();
                component.destroy();
              },
            };
          },
        },
      });
    },

    buildShortCodeExtension(shortcodes) {
      this.editorMessage = "Use [] bracket to use a shortcode";

      return Shortcodes.configure({
        HTMLAttributes: {
          class: "shortcode",
        },
        suggestion: {
          shortcodes: this.shortcodes,
          users: this.users,
          items: editor => {
            return shortcodes
              .filter(sc => {
                return sc.substring(1).slice(0, -1) // Removing the [] form the ends
                  .toLowerCase()
                  .startsWith(editor.query.toLowerCase());
              });
          },
          render: () => {
            let component;
            let popup;

            return {
              onStart: props => {
                component = new VueRenderer(ShortcodeList, {
                  parent: this,
                  propsData: props,
                });

                popup = tippy("body", {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  placement: "bottom-start",
                });
              },
              onUpdate(props) {
                component.updateProps(props);

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },
              onKeyDown(props) {
                return component.ref.onKeyDown(props);
              },
              onExit() {
                popup[0].destroy();
                component.destroy();
              },
            };
          },
        },
      });
    },

    // Options that we can eazily Add
    prepEditorOptions() {
      this.editorOptions = {
        highlight: {
          icon: "highlight",
          title: "Highlight",
          extension: Highlight,
          action: () => this.editor.chain().focus().toggleHighlight().run(),
          isActive: () => this.editor.isActive("highlight"),
        },
        strikethrough: {
          icon: "format_strikethrough",
          title: "Strike",
          extension: StarterKit,
          action: () => this.editor.chain().focus().toggleStrike().run(),
          isActive: () => this.editor.isActive("strike"),
        },
        code: {
          icon: "code-view",
          title: "Code",
          extension: StarterKit,
          action: () => this.editor.chain().focus().toggleCode().run(),
          isActive: () => this.editor.isActive("code"),
        },
        codeBlock: {
          icon: "code-box-line",
          title: "Code",
          extension: StarterKit,
          action: () => this.editor.chain().focus().toggleCodeBlock().run(),
          isActive: () => this.editor.isActive("codeBlock"),
        },
        h1: {
          icon: "h-1",
          title: "Heading 1",
          extension: StarterKit,
          action: () => this.editor.chain().focus().toggleHeading({level: 1}).run(),
          isActive: () => this.editor.isActive("heading", {level: 1}),
        },
        h2: {
          icon: "h-2",
          title: "Heading 2",
          extension: StarterKit,
          action: () => this.editor.chain().focus().toggleHeading({level: 2}).run(),
          isActive: () => this.editor.isActive("heading", {level: 2}),
        },
        paragraph: {
          icon: "paragraph",
          title: "Paragraph",
          extension: StarterKit,
          action: () => this.editor.chain().focus().setParagraph().run(),
          isActive: () => this.editor.isActive("paragraph"),
        },
        // checkList: {
        //   icon: 'checklist',
        //   title: 'Check List',
        //   extension: TaskList,
        //   action: () => this.editor.chain().focus().toggleTaskList().run(),
        //   isActive: () => this.editor.isActive('taskList'),
        // },
        blockquote: {
          icon: "double-quotes-l",
          title: "Blockquote",
          extension: StarterKit,
          action: () => this.editor.chain().focus().toggleBlockquote().run(),
          isActive: () => this.editor.isActive("blockquote"),
        },
        separator: {
          icon: "double-quotes-l",
          title: "Horizontal Rule",
          extension: StarterKit,
          action: () => this.editor.chain().focus().setHorizontalRule().run(),
        },
        textWrap: {
          icon: "text-wrap",
          title: "Hard Break",
          extension: StarterKit,
          action: () => this.editor.chain().focus().setHeardBreak().run(),
        },
        clearFormat: {
          icon: "format-clear",
          title: "Clear Format",
          extension: StarterKit,
          action: () => this.editor.chain().focus().clearNodes().unsetAllMarks().run(),
        },
        // undo: {
        //   icon: 'arrow-go-back-line',
        //   title: 'Undo',
        //   extension: StarterKit,
        //   action: () => this.editor.chain().focus().undo().run(),
        // },
        // redo: {
        //   icon: 'arrow-go-forward-line',
        //   title: 'Redo',
        //   extension: StarterKit,
        //   action: () => this.editor.chain().focus().redo().run(),
        // },
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.tiptap-wrapper {
  flex-wrap: wrap;
}

/*
 * Some styles are in styles/vendor/tiptap.scss
 * cause the code isn't available to this style section
 */

.tiptap {
  width: 100%;
  max-width: 740px;
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid $border;

  &.is-danger {
    border-color: #C62828;
  }
}

.tiptap_menu {
  margin-bottom: 20px;

  &--item {
    width: 20px;
    height: 20px;
    margin-right: 0.25rem;
    padding: 0;
    border: none;
    border-radius: 3px;
    color: $text-light;
    text-align: center;

    &.is-active {
      background-color: $grey-100;
      color: $text;
    }

    &:hover {
      color: $primary;
    }

    .material-icons {
      font-size: 16px;
      line-height: 20px;
    }
  }
}

.tiptap_divider {
  display: inline-block;
  margin: 0 4px;
  height: 20px;
  width: 1px;
  background-color: $border;
}

.tiptap_hints {
  display: flex;

  &--message,
  &--count {
    font-size: 12px;
    color: $text-light;
  }

  &--count {
    margin-left: auto;
  }
}
</style>
